import React, { createContext, useState, useEffect, useContext } from 'react';
import { auth, db } from '@root/firebase';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import logEvent from '@components/LogEvent';
import LogError from '@components/LogError';

const defaultAvatarUrl =
  'https://firebasestorage.googleapis.com/v0/b/smartdog-bbe9f.firebasestorage.app/o/images%2Fuser.png?alt=media&token=daf30f34-5f3f-4f3d-bbdc-de5b0441365b';

import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [globalUser, setGlobalUser] = useState(null);
  const [globalData, setGlobalData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [showLogs, setShowLogs] = useState(false); // True/False show console.log output

  const register = (email, password) =>
    createUserWithEmailAndPassword(auth, email, password).catch((error) => {
      showLogs && console.error('Error registering user:', error.message);
      throw error;
    });

  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;

      if (user) {
        logEvent('LOGIN SUCCESS', { email: user.email, uid: user.uid });
      } else {
        console.warn('Login successful but user object is undefined.');
      }

      return userCredential;
    } catch (error) {
      showLogs && console.error('Error logging in:', error.message);
      //logEvent('LOGIN FAILURE', { email });
      throw error;
    }
  };

  const forgot = (email) =>
    sendPasswordResetEmail(auth, email).catch((error) => {
      showLogs &&
        console.error('Error sending password reset email:', error.message);
      throw error;
    });

  const logout = () => {
    const user = auth.currentUser;
    const email = user?.email;
    const uid = user?.uid;

    console.log('+++++++ Logout Start +++++++');
    console.log('Logged In UID: ', user.uid);
    console.log('Logged In User: ', user.email);

    return signOut(auth)
      .then(() => {
        //logEvent('LOGOUT SUCCESS', { email, uid });
      })
      .catch((error) => {
        showLogs && console.error('Error logging out:', error.message);
        throw error;
      });
  };

  const fetchDashboardData = async () => {
    // Replace with your own logic to fetch data
    return { welcomeMessage: 'Welcome to your Dashboard!' };
  };

  /**
   * Fetch address subcollection for a user.
   */
  const fetchAddress = async (uid) => {
    try {
      const addressCollectionRef = collection(db, `users/${uid}/address`);
      const addressDocs = await getDocs(addressCollectionRef);
      return addressDocs.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      showLogs && console.error('Error fetching address data:', error.message);
      return [];
    }
  };

  /**
   * Monitor changes to the address subcollection.
   */
  const subscribeToAddressChanges = (uid) => {
    const addressCollectionRef = collection(db, `users/${uid}/address`);
    return onSnapshot(addressCollectionRef, (snapshot) => {
      const addressData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setGlobalData((prevData) => ({
        ...prevData,
        address: addressData,
      }));
      showLogs && console.log('Address data updated:', addressData);
    });
  };

  const fetchUserData = async (uid) => {
    try {
      // Fetch user document
      const userDocRef = doc(db, 'users', uid);
      const userDoc = await getDoc(userDocRef);

      let userData = null;

      if (userDoc.exists()) {
        userData = userDoc.data();
      } else {
        showLogs && console.warn('User document not found!');
      }

      // Fetch address subcollection
      const addressCollectionRef = collection(db, `users/${uid}/address`);
      const addressDocs = await getDocs(addressCollectionRef);

      const addressData = addressDocs.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Ensure userData is initialized even if the user doc is missing
      userData = userData || {};
      userData.address = addressData; // Merge address data

      showLogs && console.log('Fetched user data with address:', userData); // Debugging log

      return userData;
    } catch (error) {
      showLogs && console.error('Error fetching user data:', error.message);
      throw error;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setLoading(true); // Start loading state
      if (user) {
        setGlobalUser(user);
        try {
          const userData = await fetchUserData(user.uid);
          if (userData) {
            setGlobalData(userData);
            showLogs && console.log('globalData successfully set:', userData); // Debugging log
          } else {
            showLogs &&
              console.warn('No user data found globalData will be null.');
            setGlobalData(null);
          }
        } catch (error) {
          showLogs && console.error('Error fetching user data:', error.message);
          setGlobalData(null);
        }
      } else {
        setGlobalUser(null);
        setGlobalData(null);
      }
      setLoading(false); // End loading state
    });

    return () => unsubscribe();
  }, []);

  showLogs && console.log('Global User: ', globalUser);

  const value = {
    globalUser,
    globalData,
    setGlobalData,
    register,
    login,
    logout,
    forgot,
    fetchDashboardData,
    defaultAvatarUrl,
    LogError,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading ? (
        children
      ) : (
        <div id='preloader'>
          <div className='lds-ripple'>
            <div></div>
            <div></div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
}
